import React, {useState} from 'react';
import {BarcodeFormat, BrowserQRCodeReader, IBrowserCodeReaderOptions} from '@zxing/browser';
import {DecodeHintType, Result} from "@zxing/library";
import {updateSingleTableField} from "../../features/tableSlice";
import {useAppDispatch} from "../../hooks";
import styled from "styled-components";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faQrcode} from "@fortawesome/free-solid-svg-icons";

interface QrFromImageProps {
    imageUrl: string;
    value: null | string,
    id: string | number
}


const options: IBrowserCodeReaderOptions = {
    delayBetweenScanSuccess: 5000,         // wait 0.5s between scans
    delayBetweenScanAttempts: 20000, // wait 0.2s before retry on same scan
};

const decodeFromBlob = async (src: string) => {
    const hints = new Map();
    hints.set(DecodeHintType.TRY_HARDER, true);
    hints.set(DecodeHintType.POSSIBLE_FORMATS, [BarcodeFormat.QR_CODE]);
    const codeReader = new BrowserQRCodeReader(hints, options);
    const result = await codeReader.decodeFromImageUrl(src);
    return result
};

const decode = async (canvas: HTMLCanvasElement) => {
    const hints = new Map();
    hints.set(DecodeHintType.TRY_HARDER, true);
    hints.set(DecodeHintType.POSSIBLE_FORMATS, [BarcodeFormat.QR_CODE]);
    const codeReader = new BrowserQRCodeReader(hints, options);
    return codeReader.decodeFromCanvas(canvas);
};

export default function QrFromImage({imageUrl, value, id}: QrFromImageProps) {
    const [qrResult, setQrResult] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    const dispatch = useAppDispatch();
    // Load an image from a Blob, revoke URL on load/error
    const loadImageFromBlob = (blob: Blob): Promise<HTMLImageElement> => {
        const blobUrl = URL.createObjectURL(blob);
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = "anonymous"; // works if CORS is supported
            img.onload = () => {
                URL.revokeObjectURL(blobUrl);
                resolve(img);
            };
            img.onerror = (e) => {
                URL.revokeObjectURL(blobUrl);
                reject(e);
            };
            img.src = blobUrl;
        });
    };


    // Retry by cropping the top half of the image and decoding again
    const retryDecodeWithCrop = async (blob: Blob): Promise<Result> => {
        const img = await loadImageFromBlob(blob);

        const canvas = document.createElement("canvas");
        let width = canvas.width = img.naturalWidth || img.width;
        let height = img.naturalHeight || img.height;
        // canvas.height = Math.floor(height / 2); // crop height to half
        const ctx = canvas.getContext("2d");
        if (!ctx) throw new Error("Failed to get canvas context");

        // Draw top half of the image only
        const halfHeight = Math.floor(height / 2);
        canvas.height = halfHeight;
        ctx.drawImage(
            img,
            0,             // source x
            height - halfHeight,    // source y — start at bottom half
            width * 2,  // source width (full width)
            halfHeight,    // source height (bottom half)
            0,             // dest x
            0,             // dest y
            width * 2,  // dest width
            halfHeight     // dest height
        );

        return decode(canvas);
    };

    const decodeImage = async () => {
        setQrResult(null);
        setError(null);
        let resText = null
        let qr = null;
        try {
            const res = await fetch(imageUrl, {credentials: "include"});
            if (!res.ok) throw new Error(`Failed to fetch image: ${res.status}`);

            const blob = await res.blob();

            try {
                const retryResult = await decodeFromBlob(URL.createObjectURL(blob));
                qr = retryResult.getText();
                resText = getUniqueIdForCheckFromQR(qr)
                setQrResult(resText);
            } catch (firstErr) {
                console.warn("Full image decode failed, retrying with cropped half...", firstErr);
                const retryResult = await retryDecodeWithCrop(blob);
                qr = retryResult.getText();
                resText = getUniqueIdForCheckFromQR(qr)
                setQrResult(resText);
            }
        } catch (err) {
            console.error("QR decoding error:", err);
            setError(`Помилка - ${err}`);
        }
        if (resText === null)
            setQrResult("Не вдалось знайти чи зчитати QR код.\n Введіть ідентифікатор вручну")
        if (resText !== null) {
            dispatch(updateSingleTableField({field: 'unique_id', value: resText, id: id}))
            dispatch(updateSingleTableField({field: 'qr_code', value: qr, id: id}))
        }
    };
    if (value !== null) return <div>{value}</div>;

    return (
        <Container>
            <div>
                <button onClick={decodeImage}>
                    <FontAwesomeIcon icon={faQrcode} style={{width: '1.2rem', height: '1.2rem'}}
                                     className={'fa-secondary-color-text'}/>
                    Зчитати QR код
                    <FontAwesomeIcon icon={faQrcode} style={{width: '1.2rem', height: '1.2rem'}}
                                     className={'fa-secondary-color-text'}/>
                </button>
            </div>
            {(qrResult || error) && <div>
                {qrResult && <div className={'qr'}>
                    QR код:&nbsp;
                    {String(qrResult).split('\n').map((line, index) => (
                        <React.Fragment key={index}>
                            {line}
                            <br/>
                        </React.Fragment>
                    ))}
                </div>}
                {error && (
                    <div className={'qr-err'}>{error}</div>
                )}
            </div>}
        </Container>
    );
}

const Container = styled.div`
    color: var(--table-row-text-color);
    background: var(--table-row-odd-bg-color);
    display: flex;
    gap: .5rem;
    flex-flow: column;

    & .qr-err {
        color: var(--toastify-color-warning);
    }

    & button {
        width: 100%;
        height: 36px;
        background: var(--table-action-color);
        color: var(--secondary-text);
        padding: .2rem;
        cursor: pointer;
        border-radius: .2rem;
        display: flex;
        gap: .4rem;
        vertical-align: center;
        align-items: center;
        justify-content: center;
        font-size: 1rem;
        border: none;
    }
`

function getUniqueIdForCheckFromQR(input: string) {
    if (!input) return null;
    const prefix = "https://cabinet.tax.gov.ua/cashregs/check?";

    if (!input.startsWith(prefix)) return null;

    // Extract query string after '?'
    const queryString = input.substring(prefix.length);

    // Parse query params into an object
    const params = Object.fromEntries(new URLSearchParams(queryString));

    const id = params.id;
    const fn = params.fn;

    if (id && fn) return `${id} ${fn}`;

    return null;
}
